import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Slider } from "./ui/slider";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Box, RotateCw, Sun } from "lucide-react";

const ROOM_COLORS = ["#38BDF8", "#34D399", "#F59E0B", "#A855F7", "#F472B6", "#60A5FA", "#FBBF24", "#4ADE80"];

// Isometric projection of a point (x,y in feet, z height) rotated around plan center
function projFactory(angleDeg, cx, cy, scale) {
  const a = (angleDeg * Math.PI) / 180;
  const c30 = Math.cos(Math.PI / 6);
  const s30 = Math.sin(Math.PI / 6);
  return (x, y, z) => {
    const dx = x - cx;
    const dy = y - cy;
    const rx = dx * Math.cos(a) - dy * Math.sin(a);
    const ry = dx * Math.sin(a) + dy * Math.cos(a);
    const sx = (rx - ry) * c30 * scale;
    const sy = ((rx + ry) * s30 - z) * scale;
    return [sx, sy];
  };
}

export default function Walkthrough3D({ open, onOpenChange, blueprint, floors, roofStyle = "Gable", onRoofStyleChange, site }) {
  const [angle, setAngle] = useState(35);
  const [wallH, setWallH] = useState(9);
  const [sunHour, setSunHour] = useState(12);

  const floorList = (floors && floors.length) ? floors : [{ rooms: blueprint?.rooms || [] }];
  // --- fit view to the actual plot + building, and tilt massing to match the survey ---
  const _allRooms = floorList.flatMap((f) => f.rooms || []);
  const _bnd = (site?.boundary && site.boundary.length >= 3) ? site.boundary : null;
  const _rx = _allRooms.length ? _allRooms.flatMap((r) => [r.x, r.x + r.w]) : [0, 60];
  const _ry = _allRooms.length ? _allRooms.flatMap((r) => [r.y, r.y + r.h]) : [0, 40];
  const bcx = (Math.min(..._rx) + Math.max(..._rx)) / 2;
  const bcy = (Math.min(..._ry) + Math.max(..._ry)) / 2;
  const _ax = [..._rx, ...(_bnd ? _bnd.map((p) => p.x) : [])];
  const _ay = [..._ry, ...(_bnd ? _bnd.map((p) => p.y) : [])];
  const cx = (Math.min(..._ax) + Math.max(..._ax)) / 2;
  const cy = (Math.min(..._ay) + Math.max(..._ay)) / 2;
  const _spanX = Math.max(..._ax) - Math.min(..._ax);
  const _spanY = Math.max(..._ay) - Math.min(..._ay);
  const scale = Math.max(2.2, Math.min(5, 600 / (((_spanX + _spanY) || 60) * Math.cos(Math.PI / 6))));
  const SKEW = Math.atan2(8.23, 77.8); // ~6.04deg, parallel to plat side bearings
  const skew = (x, y) => {
    const dx = x - bcx, dy = y - bcy;
    return [bcx + dx * Math.cos(SKEW) - dy * Math.sin(SKEW), bcy + dx * Math.sin(SKEW) + dy * Math.cos(SKEW)];
  };
  const slopePct = site?.slope_pct || 0;
  const slopeType = site?.slope_type || "Flat";
  const sloped = slopeType !== "Flat" && slopePct > 0;
  const step = sloped ? 6 : 0;
  const floorOffset = (fi) => {
    if (slopeType === "Downhill") return { dx: -fi * step, dy: 0 };
    if (slopeType === "Uphill") return { dx: fi * step, dy: 0 };
    if (slopeType === "Cross-slope") return { dx: 0, dy: -fi * step };
    return { dx: 0, dy: 0 };
  };
  const gz = (x, y) => {
    if (!sloped) return 0;
    const k = slopePct / 100;
    if (slopeType === "Downhill") return -k * x;
    if (slopeType === "Uphill") return -k * (80 - x);
    return -k * y; // cross-slope
  };

  const scene = useMemo(() => {
    const project = projFactory(angle, cx, cy, scale);
    const items = [];
    let lvl = 0;
    floorList.forEach((fl, fi) => {
      const isFoundation = /found|slab/i.test(fl.name || "");
      if (isFoundation) return; // foundation is not an extruded story
      const isDeck = fl.deck || /deck/i.test(fl.name || "");
      const zb = lvl * wallH;
      const zt = zb + (isDeck ? 3.2 : wallH); // deck = low railing height
      lvl += isDeck ? 0 : 1;                   // deck sits on the roof, adds no story
      const { dx, dy } = floorOffset(fi);
      (fl.rooms || []).forEach((r, idx) => {
        const color = isDeck ? "#B08968" : ROOM_COLORS[(idx + fi * 3) % ROOM_COLORS.length];
        const corners = [
          [r.x + dx, r.y + dy], [r.x + r.w + dx, r.y + dy], [r.x + r.w + dx, r.y + r.h + dy], [r.x + dx, r.y + r.h + dy],
        ].map(([x, y]) => skew(x, y));
        const base = corners.map(([x, y]) => project(x, y, zb));
        const top = corners.map(([x, y]) => project(x, y, zt));
        const depth = corners.reduce((s, [x, y]) => { const [, sy] = project(x, y, zb); return s + sy; }, 0) / 4 + lvl * 0.01 + (isDeck ? 0.5 : 0);
        const walls = [];
        for (let i = 0; i < 4; i++) {
          const j = (i + 1) % 4;
          walls.push({ pts: [base[i], base[j], top[j], top[i]], shade: i % 2 === 0 ? 0.42 : 0.26 });
        }
        const [ccx, ccy] = skew(r.x + r.w / 2, r.y + r.h / 2);
        const center = project(ccx, ccy, zt);
        items.push({ color, base, top, walls, depth, center, name: r.name, isDeck });
      });
    });
    items.sort((a, b) => a.depth - b.depth);
    return items;
  }, [floorList, angle, wallH, slopeType, slopePct]);

  const ground = useMemo(() => {
    const project = projFactory(angle, cx, cy, scale);
    const lot = _bnd ? _bnd.map((p) => [p.x, p.y]) : [[-6, -6], [86, -6], [86, 66], [-6, 66]];
    const pts = lot.map(([x, y]) => project(x, y, gz(x, y)));
    let arrow = null, cut = null, fill = null;
    if (sloped) {
      const a1 = slopeType === "Cross-slope" ? [40, 8] : (slopeType === "Uphill" ? [72, 30] : [8, 30]);
      const a2 = slopeType === "Cross-slope" ? [40, 52] : (slopeType === "Uphill" ? [8, 30] : [72, 30]);
      arrow = { p1: project(a1[0], a1[1], gz(a1[0], a1[1])), p2: project(a2[0], a2[1], gz(a2[0], a2[1])) };
      const fr = (floorList[0] && floorList[0].rooms) || [];
      if (fr.length) {
        const P = (x, y) => project(x, y, gz(x, y));
        if (slopeType === "Cross-slope") {
          const yS = (Math.min(...fr.map((r) => r.y)) + Math.max(...fr.map((r) => r.y + r.h))) / 2;
          cut = [P(-6, -6), P(86, -6), P(86, yS), P(-6, yS)];
          fill = [P(-6, yS), P(86, yS), P(86, 66), P(-6, 66)];
        } else {
          const xS = (Math.min(...fr.map((r) => r.x)) + Math.max(...fr.map((r) => r.x + r.w))) / 2;
          const left = [P(-6, -6), P(xS, -6), P(xS, 66), P(-6, 66)];
          const right = [P(xS, -6), P(86, -6), P(86, 66), P(xS, 66)];
          if (slopeType === "Uphill") { cut = right; fill = left; } else { cut = left; fill = right; }
        }
      }
    }
    return { pts, arrow, cut, fill };
  }, [angle, slopeType, slopePct, floorList]);

  const shadows = useMemo(() => {
    if (!open) return [];
    const project = projFactory(angle, cx, cy, scale);
    const elev = Math.max(0.12, Math.sin(Math.PI * (sunHour - 6) / 12));
    const H = floorList.length * wallH;
    const shadowLen = Math.min(H * (1 - elev) / elev, 55);
    const az = ((sunHour - 12) / 6) * 70 * Math.PI / 180;
    const sx = shadowLen * Math.sin(az);
    const sy = shadowLen * 0.55;
    const { dx, dy } = floorOffset(0);
    return ((floorList[0] && floorList[0].rooms) || []).map((r) => {
      const c = [[r.x + dx, r.y + dy], [r.x + r.w + dx, r.y + dy], [r.x + r.w + dx, r.y + r.h + dy], [r.x + dx, r.y + r.h + dy]];
      return c.map(([x, y]) => { const [kx, ky] = skew(x, y); return project(kx + sx, ky + sy, gz(kx + sx, ky + sy)); });
    });
  }, [open, angle, wallH, sunHour, slopeType, slopePct, floorList]);

  const roof = useMemo(() => {
    const project = projFactory(angle, cx, cy, scale);
    const living = floorList.filter((f) => !/found|slab|deck/i.test(f.name || ""));
    const top = living[living.length - 1] || floorList[floorList.length - 1];
    const rs = (top && top.rooms) || [];
    if (!rs.length) return [];
    const minX = Math.min(...rs.map((r) => r.x)), minY = Math.min(...rs.map((r) => r.y));
    const maxX = Math.max(...rs.map((r) => r.x + r.w)), maxY = Math.max(...rs.map((r) => r.y + r.h));
    const z = (living.length || 1) * wallH;
    const rh = 6;
    const { dx, dy } = floorOffset(floorList.length - 1);
    const P = (x, y, zz) => { const [sx, sy] = skew(x + dx, y + dy); return project(sx, sy, zz); };
    const c1 = P(minX, minY, z), c2 = P(maxX, minY, z), c3 = P(maxX, maxY, z), c4 = P(minX, maxY, z);
    const polys = [];
    if (roofStyle === "Flat") {
      polys.push({ pts: [c1, c2, c3, c4], fill: "#484F58", op: 0.55 });
    } else if (roofStyle === "Shed") {
      const t1 = P(minX, minY, z + rh), t2 = P(maxX, minY, z + rh);
      polys.push({ pts: [c4, c3, t2, t1], fill: "#6E7681", op: 0.55 });
      polys.push({ pts: [c1, c2, t2, t1], fill: "#8B949E", op: 0.4 });
    } else if (roofStyle === "Hip") {
      const apex = P((minX + maxX) / 2, (minY + maxY) / 2, z + rh);
      polys.push({ pts: [c1, c2, apex], fill: "#6E7681", op: 0.5 });
      polys.push({ pts: [c2, c3, apex], fill: "#8B949E", op: 0.45 });
      polys.push({ pts: [c3, c4, apex], fill: "#6E7681", op: 0.5 });
      polys.push({ pts: [c4, c1, apex], fill: "#8B949E", op: 0.4 });
    } else if (roofStyle === "Gable") {
      // Twin gable: split the footprint into left/right halves, each with a ridge running front-to-back
      const midX = (minX + maxX) / 2;
      const halves = [[minX, midX], [midX, maxX]];
      halves.forEach(([x0, x1], hi) => {
        const cxh = (x0 + x1) / 2;
        const a = P(x0, minY, z), b = P(x1, minY, z), cc = P(x1, maxY, z), dd = P(x0, maxY, z);
        const r1 = P(cxh, minY, z + rh), r2 = P(cxh, maxY, z + rh);
        polys.push({ pts: [a, dd, r2, r1], fill: hi ? "#8B949E" : "#6E7681", op: 0.5 });   // left slope
        polys.push({ pts: [b, cc, r2, r1], fill: hi ? "#6E7681" : "#8B949E", op: 0.55 });  // right slope
        polys.push({ pts: [a, b, r1], fill: "#3A4048", op: 0.7 });                          // front gable end
        polys.push({ pts: [dd, cc, r2], fill: "#3A4048", op: 0.7 });                        // back gable end
      });
    } else {
      const midY = (minY + maxY) / 2;
      const r1 = P(minX, midY, z + rh), r2 = P(maxX, midY, z + rh);
      polys.push({ pts: [c1, c2, r2, r1], fill: "#8B949E", op: 0.45 });
      polys.push({ pts: [c4, c3, r2, r1], fill: "#6E7681", op: 0.55 });
      polys.push({ pts: [c1, c4, r1], fill: "#484F58", op: 0.6 });
      polys.push({ pts: [c2, c3, r2], fill: "#484F58", op: 0.6 });
    }
    return polys;
  }, [floorList, angle, wallH, roofStyle, slopeType, slopePct]);

  const poly = (pts) => pts.map((p) => p.join(",")).join(" ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <Box className="w-5 h-5 text-[#A855F7]" /> 3D Walkthrough
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">A simple massing preview of your floor plan — rotate and adjust wall height.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-[#30363D] bg-[#090D12] overflow-hidden" data-testid="walkthrough-3d-scene">
          <svg viewBox={`-360 ${-300 - (floorList.length - 1) * 48} 720 ${560 + (floorList.length - 1) * 48}`} width="100%" height="380" style={{ display: "block" }}>
            <defs>
              <pattern id="grassPat" width="10" height="10" patternUnits="userSpaceOnUse">
                <rect width="10" height="10" fill="#12301c" />
                <path d="M2 9 L2 4 M5 9 L5 5 M8 9 L8 3" stroke="#1f5130" strokeWidth="1" />
              </pattern>
              <pattern id="rockPat" width="12" height="12" patternUnits="userSpaceOnUse">
                <rect width="12" height="12" fill="#3a3128" />
                <circle cx="3" cy="4" r="1.6" fill="#4d4235" />
                <circle cx="8" cy="8" r="1.2" fill="#5a4d3c" />
              </pattern>
            </defs>
            {/* ground (grass) + cut(rock)/fill zones */}
            <polygon points={poly(ground.pts)} fill="url(#grassPat)" stroke="#30363D" strokeWidth="1.5" />
            {sloped && (
              <>
                {ground.cut && <polygon points={poly(ground.cut)} fill="url(#rockPat)" />}
                {ground.cut && <polygon points={poly(ground.cut)} fill="rgba(245,158,11,0.16)" stroke="#F59E0B" strokeOpacity="0.5" strokeWidth="1" />}
                {ground.fill && <polygon points={poly(ground.fill)} fill="rgba(52,211,153,0.16)" stroke="#34D399" strokeOpacity="0.5" strokeWidth="1" />}
                {ground.arrow && (
                  <g stroke="#F43F5E" strokeWidth="2" fill="none" opacity="0.9">
                    <line x1={ground.arrow.p1[0]} y1={ground.arrow.p1[1]} x2={ground.arrow.p2[0]} y2={ground.arrow.p2[1]} />
                    <circle cx={ground.arrow.p2[0]} cy={ground.arrow.p2[1]} r="4" fill="#F43F5E" />
                    <text x={ground.arrow.p1[0]} y={ground.arrow.p1[1] - 8} fill="#F43F5E" fontSize="11" fontFamily="JetBrains Mono, monospace" textAnchor="middle" stroke="none">↓ {slopePct}%</text>
                  </g>
                )}
              </>
            )}
            {shadows.map((s, i) => (
              <polygon key={`sh${i}`} points={poly(s)} fill="rgba(0,0,0,0.32)" />
            ))}
            {scene.map((r, i) => (
              <g key={i}>
                {r.isDeck ? (
                  <>
                    <polygon points={poly(r.base)} fill={r.color} fillOpacity="0.5" stroke={r.color} strokeOpacity="0.9" strokeWidth="1.5" />
                    {r.walls.map((w, k) => (
                      <polygon key={k} points={poly(w.pts)} fill={r.color} fillOpacity="0.12" stroke={r.color} strokeOpacity="0.85" strokeWidth="1" strokeDasharray="3 2" />
                    ))}
                    <polygon points={poly(r.top)} fill="none" stroke={r.color} strokeOpacity="0.95" strokeWidth="1.5" />
                  </>
                ) : (
                  <>
                    <polygon points={poly(r.base)} fill="rgba(9,13,18,0.6)" stroke={r.color} strokeOpacity="0.3" strokeWidth="1" />
                    {r.walls.map((w, k) => (
                      <polygon key={k} points={poly(w.pts)} fill={r.color} fillOpacity={w.shade} stroke={r.color} strokeOpacity="0.5" strokeWidth="1" />
                    ))}
                    <polygon points={poly(r.top)} fill={r.color} fillOpacity="0.18" stroke={r.color} strokeOpacity="0.9" strokeWidth="1.5" />
                  </>
                )}
                <text x={r.center[0]} y={r.center[1] - 4} fill="#F0F6FC" fontSize="10" fontFamily="Outfit, sans-serif" fontWeight="600" textAnchor="middle">{r.name}</text>
              </g>
            ))}
            {roof.map((p, i) => (
              <polygon key={`roof${i}`} points={poly(p.pts)} fill={p.fill} fillOpacity={p.op} stroke="#30363D" strokeWidth="1" />
            ))}
          </svg>
        </div>
        {sloped && (
          <div className="flex items-center gap-4 text-[10px] font-mono-cad text-[#8B949E] pt-1" data-testid="cutfill-legend">
            <span className="flex items-center gap-1"><span className="w-3 h-2 inline-block" style={{ background: "rgba(245,158,11,0.5)" }} /> Cut (into hill)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 inline-block" style={{ background: "rgba(52,211,153,0.5)" }} /> Fill (built up)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 inline-block bg-black/40" /> Shadow</span>
          </div>
        )}
        <div className="grid grid-cols-4 gap-4 pt-2">
          <div>
            <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><RotateCw className="w-3 h-3" /> Rotate ({Math.round(angle)}°)</Label>
            <Slider value={[angle]} min={0} max={360} step={1} onValueChange={(v) => setAngle(v[0])} className="mt-3" data-testid="rotate-slider" />
          </div>
          <div>
            <Label className="text-xs text-[#8B949E]">Wall Height ({wallH} ft)</Label>
            <Slider value={[wallH]} min={8} max={16} step={1} onValueChange={(v) => setWallH(v[0])} className="mt-3" data-testid="wall-height-slider" />
          </div>
          <div>
            <Label className="text-xs text-[#8B949E] flex items-center gap-1.5"><Sun className="w-3 h-3" /> Sun ({sunHour}:00)</Label>
            <Slider value={[sunHour]} min={6} max={18} step={1} onValueChange={(v) => setSunHour(v[0])} className="mt-3" data-testid="sun-slider" />
          </div>
          <div>
            <Label className="text-xs text-[#8B949E]">Roof Style</Label>
            <Select value={roofStyle} onValueChange={(v) => onRoofStyleChange && onRoofStyleChange(v)}>
              <SelectTrigger data-testid="roof-style-select" className="bg-[#0D1117] border-[#30363D] mt-2 h-8"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                {["Gable", "Hip", "Flat", "Shed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
